import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { authedFetch } from "@/lib/auth";
import { AUTHORITY_LEVELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ActionBar } from "@/components/layout/action-bar";
import { Page } from "@/components/layout/page";
import { Loader2 } from "lucide-react";

type SubmitState =
  | { phase: "form" }
  | { phase: "submitting" }
  | {
      phase: "done";
      sourceId: string;
      status: "ready" | "error";
      errorMessage?: string;
    };

export function ManualEntryPage() {
  const [searchParams] = useSearchParams();
  const editSourceId = searchParams.get("sourceId");
  const prefillUrl = searchParams.get("url");
  const prefillName = searchParams.get("name");
  const prefillAuthority = searchParams.get("authorityLevel");
  const prefill = searchParams.get("prefill");

  const [name, setName] = useState(prefillName ?? prefill ?? "");
  const [content, setContent] = useState("");
  const [authorityLevel, setAuthorityLevel] = useState(prefillAuthority ?? "");
  const [language, setLanguage] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>({
    phase: "form",
  });
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 5.2: Edit mode — fetch source data when sourceId is provided
  const { data: sourceData } = trpc.source.getById.useQuery(
    { id: editSourceId! },
    { enabled: !!editSourceId },
  );

  useEffect(() => {
    if (sourceData && editSourceId) {
      setName(sourceData.name);
      setAuthorityLevel(sourceData.authorityLevel);
      setLanguage(sourceData.language ?? "");
    }
  }, [sourceData, editSourceId]);

  // Fetch parsed content for edit mode
  useEffect(() => {
    if (!editSourceId || !sourceData?.versions?.length) return;

    const controller = new AbortController();
    const latestVersion = sourceData.versions[sourceData.versions.length - 1];
    authedFetch(
      `/api/sources/${editSourceId}/parsed-content?versionId=${latestVersion.id}`,
      {
        signal: controller.signal,
      },
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.text) setContent(data.text);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [editSourceId, sourceData]);

  const canSubmit = name.trim() && content.trim() && authorityLevel;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitError(null);
    setSubmitState({ phase: "submitting" });

    try {
      const res = await authedFetch("/api/ingest/manual", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          content: content.trim(),
          authorityLevel,
          language: language.trim() || undefined,
          sourceId: editSourceId || undefined,
          url: prefillUrl || undefined,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        setSubmitError(body.error ?? `Request failed (${res.status})`);
        setSubmitState({ phase: "form" });
        return;
      }

      const { sourceId } = await res.json();
      setSubmitState({ phase: "done", sourceId, status: "ready" });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Request failed");
      setSubmitState({ phase: "form" });
    }
  }

  function resetForm() {
    setName("");
    setContent("");
    setAuthorityLevel("");
    setLanguage("");
    setSubmitState({ phase: "form" });
    setSubmitError(null);
  }

  const pageTitle = editSourceId ? "Edit Manual Entry" : "Manual Entry";

  if (submitState.phase === "done") {
    return (
      <Page title={pageTitle}>
        <Card>
          <CardContent className="py-12 text-center">
            {submitState.status === "ready" ? (
              <>
                <Badge className="mb-4 bg-mark-helps/8 text-finding-helps">
                  Success
                </Badge>
                <p className="text-md font-medium">
                  {editSourceId
                    ? "Entry updated successfully!"
                    : "Entry created successfully!"}
                </p>
                <ActionBar
                  className="mt-4 justify-center"
                  align="start"
                  primary={
                    <Button asChild>
                      <Link to={`/sources/${submitState.sourceId}`}>
                        View source
                      </Link>
                    </Button>
                  }
                  secondary={[
                    <Button key="another" onClick={resetForm}>
                      Create another
                    </Button>,
                  ]}
                />
              </>
            ) : (
              <>
                <Badge className="mb-4 bg-mark-critical/8 text-severity-critical">
                  Error
                </Badge>
                <p className="text-md font-medium">Processing failed</p>
                {submitState.errorMessage && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {submitState.errorMessage}
                  </p>
                )}
                <div className="mt-4">
                  <Button variant="outline" onClick={resetForm}>
                    Try again
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </Page>
    );
  }

  return (
    <Page title={pageTitle}>
      {submitError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>{editSourceId ? "Update Entry" : "New Entry"}</CardTitle>
            <CardDescription>
              {editSourceId
                ? "Edit the content and metadata for this source."
                : "Write or paste domain knowledge directly. Markdown supported."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name your content"
              />
            </div>

            {prefillUrl && (
              <div className="rounded-md border bg-muted/50 p-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Source URL
                </p>
                <p className="text-sm truncate">{prefillUrl}</p>
              </div>
            )}

            <div className="space-y-2">
              <div>
                <Label htmlFor="content">Content</Label>
                <p className="text-sm text-muted-foreground">
                  Use Markdown headings to create structure for better chunking.
                </p>
              </div>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write your domain rules, policies, or knowledge here..."
                className="min-h-[200px]"
              />
              <p className="text-sm text-muted-foreground text-right">
                {content.length.toLocaleString()} / 50,000
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="manual-authority">Authority level</Label>
              <Select value={authorityLevel} onValueChange={setAuthorityLevel}>
                <SelectTrigger id="manual-authority">
                  <SelectValue placeholder="Select authority level" />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORITY_LEVELS.map((a) => (
                    <SelectItem key={a.value} value={a.value}>
                      {a.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="language">
                Language{" "}
                <span className="text-muted-foreground font-normal">
                  (optional)
                </span>
              </Label>
              <Input
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="e.g. english, swedish"
              />
            </div>
          </CardContent>
          <CardFooter className="border-t">
            <Button
              type="submit"
              disabled={!canSubmit || submitState.phase === "submitting"}
            >
              {submitState.phase === "submitting" ? (
                <>
                  <Loader2 className="animate-spin" />
                  Processing...
                </>
              ) : editSourceId ? (
                "Save Changes"
              ) : (
                "Create Entry"
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Page>
  );
}
